import { useState } from 'react';
import toast from 'react-hot-toast';
import { User, Lock, Info } from 'lucide-react';
import { settingsApi } from '../../api/settings.js';

const SECTIONS = [
  { id: 'profile',  label: 'Profile',  Icon: User },
  { id: 'security', label: 'Security', Icon: Lock },
  { id: 'about',    label: 'About',    Icon: Info },
];

export default function SettingsPage({ user }) {
  const [section, setSection] = useState('profile');

  // ── Profile ──────────────────────────────────────────────────────────────
  const [profile, setProfile] = useState({
    name:  user?.name  || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });
  const [profileSaving, setProfileSaving] = useState(false);

  const handleProfileSave = async () => {
    if (!profile.name.trim()) { toast.error('Name is required'); return; }
    if (!profile.email.trim()) { toast.error('Email is required'); return; }
    setProfileSaving(true);
    try {
      await settingsApi.updateProfile({
        name:  profile.name.trim(),
        email: profile.email.trim(),
        phone: profile.phone.trim(),
      });
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.message || 'Failed to update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  // ── Security ─────────────────────────────────────────────────────────────
  const [pw, setPw] = useState({ current: '', newPass: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);

  const handlePasswordChange = async () => {
    if (!pw.current)          { toast.error('Enter your current password'); return; }
    if (pw.newPass.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (pw.newPass !== pw.confirm) { toast.error('Passwords do not match'); return; }
    setPwSaving(true);
    try {
      await settingsApi.changePassword({
        current_password: pw.current,
        new_password:     pw.newPass,
      });
      toast.success('Password changed');
      setPw({ current: '', newPass: '', confirm: '' });
    } catch (err) {
      toast.error(err.message || 'Failed to change password');
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div className="page-body">
      <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 20, alignItems: 'start' }}>

        {/* Sidebar */}
        <div className="card" style={{ height: 'fit-content' }}>
          <div className="card-body" style={{ padding: 8 }}>
            {SECTIONS.map(s => (
              <button
                key={s.id}
                onClick={() => setSection(s.id)}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '9px 12px',
                  background: section === s.id ? 'var(--primary-light, #eff6ff)' : 'none',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  color: section === s.id ? 'var(--primary)' : 'var(--text)',
                  fontWeight: section === s.id ? 600 : 400,
                  fontSize: 13,
                  textAlign: 'left',
                  transition: 'all 0.15s',
                }}
              >
                <s.Icon size={15} />
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div>

          {/* ── Profile ─────────────────────────────────────────────────── */}
          {section === 'profile' && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">Profile</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Admin account information</span>
              </div>
              <div className="card-body">
                <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {/* Avatar */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, minWidth: 100 }}>
                    <div style={{
                      width: 72, height: 72, borderRadius: '50%',
                      background: 'var(--primary)', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 28, fontWeight: 700,
                    }}>
                      {profile.name.charAt(0).toUpperCase() || 'A'}
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                      {user?.role || 'admin'}
                    </span>
                  </div>

                  {/* Fields */}
                  <div style={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Full Name <span style={{ color: 'var(--danger)' }}>*</span></label>
                      <input
                        className="form-control"
                        value={profile.name}
                        onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
                        placeholder="Full name"
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Email <span style={{ color: 'var(--danger)' }}>*</span></label>
                      <input
                        className="form-control"
                        type="email"
                        value={profile.email}
                        onChange={e => setProfile(p => ({ ...p, email: e.target.value }))}
                        placeholder="Email address"
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label">Phone</label>
                      <input
                        className="form-control"
                        value={profile.phone}
                        onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))}
                        placeholder="Phone number"
                      />
                    </div>
                    <div>
                      <button
                        className="btn btn-primary"
                        onClick={handleProfileSave}
                        disabled={profileSaving}
                      >
                        {profileSaving ? 'Saving…' : 'Save Profile'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Security ────────────────────────────────────────────────── */}
          {section === 'security' && (
            <div className="card">
              <div className="card-header">
                <span className="card-title">Security</span>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Change your password</span>
              </div>
              <div className="card-body">
                <div style={{ maxWidth: 380, display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Current Password</label>
                    <input
                      className="form-control"
                      type="password"
                      value={pw.current}
                      onChange={e => setPw(p => ({ ...p, current: e.target.value }))}
                      placeholder="Enter current password"
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">New Password</label>
                    <input
                      className="form-control"
                      type="password"
                      value={pw.newPass}
                      onChange={e => setPw(p => ({ ...p, newPass: e.target.value }))}
                      placeholder="Min. 6 characters"
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">Confirm New Password</label>
                    <input
                      className="form-control"
                      type="password"
                      value={pw.confirm}
                      onChange={e => setPw(p => ({ ...p, confirm: e.target.value }))}
                      placeholder="Repeat new password"
                    />
                  </div>
                  <div>
                    <button
                      className="btn btn-primary"
                      onClick={handlePasswordChange}
                      disabled={pwSaving}
                    >
                      {pwSaving ? 'Changing…' : 'Change Password'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── About ───────────────────────────────────────────────────── */}
          {section === 'about' && (
            <div className="card">
              <div className="card-header"><span className="card-title">About</span></div>
              <div className="card-body">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                      width: 56, height: 56, borderRadius: 14,
                      background: 'var(--primary)', color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 22, fontWeight: 800,
                    }}>M</div>
                    <div>
                      <div style={{ fontFamily: 'Syne, sans-serif', fontSize: 22, fontWeight: 800, lineHeight: 1.1 }}>
                        <span>M</span>eetel
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Sales Management System</div>
                    </div>
                  </div>

                  {[
                    ['Version',   '1.0.0'],
                    ['Platform',  'Web Application'],
                    ['Contact',   'support@meetel.in'],
                  ].map(([label, value]) => (
                    <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                      <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                      <span style={{ fontWeight: 500 }}>{value}</span>
                    </div>
                  ))}

                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                    © 2024 Meetel. All rights reserved.
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
